import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { SearchPage } from "@/features/directory/modules/search-page/search-page";
import { directoryCitiesRequest, loadSearchPage } from "@/features/directory/utils/load-search-page";
import { searchHead, searchTitle } from "@/features/directory/utils/search-head";
import { ALL_SERVICES, cityFromPath, citySlug, serviceFromPath } from "@/features/directory/utils/search-path";
import { reportApiOutage } from "@/lib/api-outage";
import { localizePath } from "@/lib/locale";
import { getLocale } from "@/paraglide/runtime.js";

/**
 * A service in a city, or everyone in a city under `all`: `/search/manual-therapy/krakow`. A page
 * of its own for search engines, the way "terapia manualna Kraków" is on ZnanyLekarz (ADR 0042).
 * A city nobody is listed in has no page. An address written another way moves to the one we use.
 */
export const Route = createFileRoute("/(public)/search/$service/$city/")({
  staticData: { ownAlternates: true },
  loader: async ({ params }) => {
    const service = serviceFromPath(params.service);
    if (!service && params.service !== ALL_SERVICES) throw notFound();

    const { data: cities } = await directoryCitiesRequest().send();
    // Without the list there is no telling which city this is, which is not the same as there being none.
    if (!cities) reportApiOutage();
    const city = cityFromPath(params.city, cities ?? []);
    if (cities && !city) throw notFound();

    const slug = citySlug(city?.city ?? params.city);
    if (slug !== params.city) {
      throw redirect({ href: localizePath(`/search/${params.service}/${slug}`, getLocale()), statusCode: 301 });
    }

    const search = { ...(service ? { service } : {}), city: city?.city ?? params.city };
    return { crumb: searchTitle(search), search, cities: cities ?? [], ...(await loadSearchPage(search)) };
  },
  head: ({ loaderData }) =>
    loaderData ? searchHead({ search: loaderData.search, total: loaderData.result?.total ?? 0 }) : {},
  component: CityRoute,
});

function CityRoute() {
  const { search, result, firstTimes, cities } = Route.useLoaderData();
  return <SearchPage search={search} result={result} firstTimes={firstTimes} cities={cities} />;
}
